import * as React from 'react';
import { LocalDate } from "@js-joda/core";
import { defined, WeatherConditionType, WeatherUnit } from "@unifocus/js-utils";
import { BrokenClouds, ClearSky, FewClouds, FreezingRain, Mist, Rain, ScatteredClouds, ShowerRain, Snow, Thunderstorm, Unknown } from './SvgWeatherIcons';

/**
 * {@docCategory Weather}
 */
export interface WeatherDayProps {
   date: LocalDate;
   minTemp?: number;
   maxTemp?: number;
   units?: WeatherUnit;
   condition?: WeatherConditionType;
}

/**
 * {@docCategory Weather}
 */
export interface WeatherProps {
   weatherDay?: WeatherDayProps,
   height?: number | string,
   iconOnly?: boolean;
}

const MISSING_TEMP = '--';

const weatherIconProps = {
   width: '100%',
   height: '100%',
   className: 'unifocus-weather-icon'
}

const weatherSvgs = new Map([
   [WeatherConditionType.BROKEN_CLOUDS, <BrokenClouds {...weatherIconProps} key={WeatherConditionType.BROKEN_CLOUDS}/>],
   [WeatherConditionType.CLEAR_SKY, <ClearSky {...weatherIconProps} key={WeatherConditionType.CLEAR_SKY}/>],
   [WeatherConditionType.FEW_CLOUDS, <FewClouds {...weatherIconProps} key={WeatherConditionType.FEW_CLOUDS}/>],
   [WeatherConditionType.FREEZING_RAIN, <FreezingRain {...weatherIconProps} key={WeatherConditionType.FREEZING_RAIN}/>],
   [WeatherConditionType.MIST, <Mist {...weatherIconProps} key={WeatherConditionType.MIST}/>],
   [WeatherConditionType.RAIN, <Rain {...weatherIconProps} key={WeatherConditionType.RAIN}/>],
   [WeatherConditionType.SCATTERED_CLOUDS, <ScatteredClouds {...weatherIconProps} key={WeatherConditionType.SCATTERED_CLOUDS}/>],
   [WeatherConditionType.SHOWER_RAIN, <ShowerRain {...weatherIconProps} key={WeatherConditionType.SHOWER_RAIN}/>],
   [WeatherConditionType.SNOW, <Snow {...weatherIconProps} key={WeatherConditionType.SNOW}/>],
   [WeatherConditionType.THUNDERSTORM, <Thunderstorm {...weatherIconProps} key={WeatherConditionType.THUNDERSTORM}/>],
   [WeatherConditionType.UNKNOWN, <Unknown {...weatherIconProps} key={WeatherConditionType.UNKNOWN}/>],
   [WeatherConditionType.HAIL, <Unknown {...weatherIconProps} key={WeatherConditionType.HAIL}/>],
   [WeatherConditionType.WINDY, <Unknown {...weatherIconProps} key={WeatherConditionType.WINDY}/>],
   [WeatherConditionType.SMOKE, <Unknown {...weatherIconProps} key={WeatherConditionType.SMOKE}/>],
   [WeatherConditionType.TORNADO, <Unknown {...weatherIconProps} key={WeatherConditionType.TORNADO}/>]
]);

export const Weather = (props: WeatherProps) => {

   const weatherDay = props.weatherDay;
   const unitsString = weatherDay && weatherDay.units ? weatherDay.units : '';
   const maxTempString = weatherDay && weatherDay.maxTemp ? weatherDay.maxTemp : MISSING_TEMP;
   const minTempString = weatherDay && weatherDay.minTemp ? weatherDay.minTemp : MISSING_TEMP;
   const weatherCondition = weatherDay ? weatherDay.condition : undefined;
   const WeatherSvg = weatherCondition ? weatherSvgs.get(weatherCondition) : <Unknown  {...weatherIconProps}/>;
   const height = convertSize(props.height) || '32px';
   const iconOnly = props.iconOnly || false;

   return (
      <div className="unifocus-weather" style={{ height: height, lineHeight: height }}>
         <div className={'unifocus-weather-icon-wrapper'} style={{ width: height, height: '100%' }}>{WeatherSvg}</div>
         {iconOnly ? null :
          <div className={'unifocus-weather-min-max-temp'}>
             <span className={'unifocus-weather-max-temp'}> {maxTempString}&#176; {unitsString}</span>
             <span className={'unifocus-weather-min-max-temp-separator'}>/</span>
             <span className={'unifocus-weather-min-temp'}>{minTempString}&#176; {unitsString}</span>
          </div>
         }
      </div>
   );
}

const convertSize = (size?: string | number) => {
   if (!defined(size)) {
      return undefined;
   }

   if (typeof size === 'string' || size === 0) {
      return size;
   }

   return size + 'px';
};
